import { CircularProgress, makeStyles } from '@material-ui/core';
import React, { CSSProperties, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';


const useStyle = makeStyles(() => {
    return {
        root: {
            width         : '100%',
            height        : '100%',
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'center',
        },
    };
});


export interface LazyCompProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties;

    // Whether or not to disable the lazy load feature and show the children right away. Default is FALSE. Why do we need this? and why don't
    // we just NOT wrap the children around the LazyComp instead if we don't want to use this feature? We need this because sometime
    // we need to lazy load a component BUT we need to turn it off based on some state. E.g.
    //      <LazyComp disabled={isInEditMode}>
    //          children
    //      </LazyComp>
    readonly disabled?: boolean | undefined;
}


export const LazyComp: React.FC<LazyCompProps & PropsWithChildren<React.ReactNode>> = (
    props: LazyCompProps & PropsWithChildren<React.ReactNode>,
) => {
    const classes = useStyle();

    /**
     * For optimization, we will not display the children right away. We will create a dummy div element as a placeholder.
     * We will keep monitoring this component's position whenever the user scroll or resize the window. When this dummy element's position is
     * in the viewport, we will then set the isCompVisible to true which will then show the children.
     */

    // A reference to the dummy placeholder DIV element
    const placeholderElementRef = useRef<HTMLDivElement>(null);

    // A state to keep track of the component's visibility. We will initialize it with false, NOT visible, unless the
    // props.disabled is specifically set ton true
    const [isCompVisible, setIsCompVisible] = useState(props.disabled === true);

    const checkCompVisibility = useCallback(() => {
        if (!isCompVisible && placeholderElementRef.current) {
            const viewportBounds = document.body.getBoundingClientRect();
            const viewport = {
                x1: viewportBounds.x,                           // Top/Left corner
                y1: viewportBounds.y,
                x2: viewportBounds.x + viewportBounds.width,    // Bottom/Right corner
                y2: viewportBounds.y + viewportBounds.height,
            };

            const compBoundsRect = placeholderElementRef.current.getBoundingClientRect();
            const compBounds = {
                x1: compBoundsRect.x,                           // Top/Left corner
                y1: compBoundsRect.y,
                x2: compBoundsRect.x + compBoundsRect.width,        // Bottom/Right corner
                y2: compBoundsRect.y + compBoundsRect.height,
            };

            // If this component is in the viewport, show the Children. The component is in the viewport if it intersect the viewport vertically and
            // horizontally. Here is an example when the component intersect the viewport HORIZONTALLY.
            //      |--------------------|
            //      |      component     |
            //      |--------------------|
            //              |--------------------|
            //              |                    |
            //              |      viewport      |
            //              |                    |
            //              |--------------------|
            // Testing if a component intersect the viewport vertically is the same, instead of comparing X, we compare Y.
            const intersectedHorizontally = compBounds.x1 <= viewport.x2 && compBounds.x2 >= viewport.x1;
            const intersectedVertically = compBounds.y1 <= viewport.y2 && compBounds.y2 >= viewport.y1;
            if (intersectedHorizontally && intersectedVertically) {
                setIsCompVisible(true);
            }
        }
    }, [isCompVisible]);


    useEffect(() => {
        // Listen to the browser scroll and resize event and when the event occur, check the component position to see if it is in the view
        // However, this will only check the component's visibility when one of these event occur.
        window.addEventListener('scroll', checkCompVisibility, true);
        window.addEventListener('resize', checkCompVisibility, true);

        // checkCompVisibility ONCE on component init
        if (!isCompVisible) {
            checkCompVisibility();
        }

        // On component unload, unsubscribe the scroll and resize event
        return () => {
            window.removeEventListener('scroll', checkCompVisibility, true);
            window.removeEventListener('resize', checkCompVisibility, true);
        };
    }, [checkCompVisibility, isCompVisible]);


    if (!isCompVisible) {
        /*
            Create a dummy div before we show the children. We will monitor this div to see when it appear in the viewport. When it is
            in the viewport, we will set `isCompVisible` to true which will remove this dummy element and add the actual children components
        */
        return (
            <div
                className={clsx(props.className, classes.root)}
                ref={placeholderElementRef}
                style={props.style}
            >
                <CircularProgress />
            </div>
        );
    }


    return (
        <>
            {props.children}
        </>
    );
};
